import React, { useContext, useEffect, useRef, useState } from 'react';

import { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { UserDataContext } from 'lane-shared/contexts';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { ActiveChannelTypeEnum } from 'constants-channel';
import {
  Button,
  Table,
  NativeFilterTypes,
  FilterType,
  PageSizeType,
} from 'design-system-web';
import { Toggle } from 'components/form';
import { Loading } from 'components/general';
import { H3, H4, M, S } from 'components/typography';

import { BulkModulePreviewModal } from './components/BulkModulePreviewModal';
import { ParentChannelPicker } from './components/ParentChannelPicker';
import {
  useBulkChannelSelection,
  SelectionMode,
  ChannelListItem,
} from './hooks/useBulkChannelSelection';
import { useBulkChannelModulePreview } from './hooks/useBulkChannelModulePreview';
import { useApplyBulkModuleEnable } from './hooks/useApplyBulkModuleEnable';
import { inspectionsModule } from './registry/bulkModuleRegistry';

import styles from './styles.scss';

/**
 * Super-user tool for bulk-enabling channel modules across many channels at once.
 *
 * Gated by `FeatureFlag.BulkChannelModules`. The Inspections toggle is also gated
 * by `FeatureFlag.Inspections` to match the per-channel configuration page.
 */
export default function BulkChannelModulesPage() {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);

  const isBulkModulesEnabled = useFlag(FeatureFlag.BulkChannelModules, false);
  const isInspectionsEnabled = useFlag(FeatureFlag.Inspections, false);

  const [inspectionsToggled, setInspectionsToggled] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [applyOutcome, setApplyOutcome] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  const selection = useBulkChannelSelection();
  const preview = useBulkChannelModulePreview(
    selection.selectedChannels,
    inspectionsModule
  );
  const applyHook = useApplyBulkModuleEnable();

  const CHANNEL_TYPE_FILTERS: FilterType[] = [
    {
      key: 'type',
      type: NativeFilterTypes.Multiselect,
      label: t(
        'web.admin.portalManagement.channelManagement.column.channelType'
      ),
      options: Object.keys(ActiveChannelTypeEnum).map(type => ({
        label: type,
        value: type,
      })),
    },
  ];

  const autoSelectedForParentRef = useRef<string | null>(null);

  // Reset the auto-select guard when the parent is deselected (e.g. on mode change)
  useEffect(() => {
    if (!selection.parentChannelId) {
      autoSelectedForParentRef.current = null;
    }
  }, [selection.parentChannelId]);

  // Auto-select descendants only once per parent channel selection.
  // Guarded by a ref so Apollo background re-fetches (which create new array
  // identities) don't overwrite manual deselections the user made.
  useEffect(() => {
    if (
      selection.mode === 'parent' &&
      selection.parentDescendants.length > 0 &&
      autoSelectedForParentRef.current !== selection.parentChannelId
    ) {
      autoSelectedForParentRef.current = selection.parentChannelId;
      selection.autoSelectDescendants();
    }
  }, [
    selection.mode,
    selection.parentDescendants,
    selection.parentChannelId,
    selection.autoSelectDescendants,
  ]);

  if (!user?.isSuperUser || !isBulkModulesEnabled) {
    return null;
  }

  const handleOpenPreview = () => {
    if (selection.selectedChannelIds.length === 0) return;
    setIsPreviewOpen(true);
  };

  const handleApply = async () => {
    try {
      const result = await applyHook.apply(
        preview.toEnableIds,
        inspectionsModule
      );

      setApplyOutcome({
        succeeded: result.succeededIds.length,
        failed: result.failedIds.length,
      });
      setIsPreviewOpen(false);
      selection.clearSelection();
      setInspectionsToggled(false);
      // Defer so Apollo finishes normalizing mutation responses before the
      // query re-fires and reads from the cache.
      setTimeout(selection.refetchChannels, 0);
    } catch (err) {
      console.error('[BulkChannelModules] apply failed', err);
      window.Toast.show(
        t('web.admin.portalManagement.channelModulesBulk.apply.error')
      );
      setIsPreviewOpen(false);
    }
  };

  const handleClosePreview = () => {
    if (!applyHook.applying) {
      setIsPreviewOpen(false);
    }
  };

  const handleModeChange = (mode: SelectionMode) => {
    selection.handleModeChange(mode);
    setInspectionsToggled(false);
    setApplyOutcome(null);
    applyHook.reset();
  };

  const handleKeywordFilterChange = (keyword: string) => {
    selection.handleCriteriaChange({ keyword });
  };

  const handleFilterChange = (filters: ColumnFiltersState) => {
    const typeFilter = filters.find(f => f.id === 'type');
    const rawValue = typeFilter?.value;
    const channelTypes = Array.isArray(rawValue)
      ? rawValue.filter((v): v is string => typeof v === 'string')
      : [];
    selection.handleCriteriaChange({ channelTypes });
  };

  // ── Criteria table columns ────────────────────────────────────────────────

  const tableColumns = [
    {
      key: 'name',
      header: t(
        'web.admin.portalManagement.channelModulesBulk.table.column.name'
      ),
      disableSorting: true,
    },
    {
      key: 'type',
      header: t(
        'web.admin.portalManagement.channelModulesBulk.table.column.type'
      ),
      disableSorting: true,
    },
    {
      key: 'parentName',
      header: t(
        'web.admin.portalManagement.channelModulesBulk.table.column.parent'
      ),
      disableSorting: true,
    },
  ];

  const toTableRow = (ch: ChannelListItem) => ({
    _id: ch._id,
    name: ch.name,
    type: ch.type,
    parentName: ch.parent?.name ?? '—',
  });

  const criteriaTableRows = selection.criteriaChannels.map(toTableRow);
  const parentTableRows = selection.parentDescendants.map(toTableRow);

  const canApply =
    isInspectionsEnabled &&
    inspectionsToggled &&
    selection.selectedChannelIds.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H3 mb={2}>
          {t('web.admin.portalManagement.channelModulesBulk.page.title')}
        </H3>
        <M>
          {t('web.admin.portalManagement.channelModulesBulk.page.description')}
        </M>
      </div>

      {/* ── Apply outcome banner ─────────────────────────────────────────── */}
      {applyOutcome && (
        <div
          className={
            applyOutcome.failed > 0 ? styles.errorBanner : styles.successBanner
          }
        >
          <M>
            {t(
              applyOutcome.failed > 0
                ? 'web.admin.portalManagement.channelModulesBulk.apply.partialSuccess'
                : 'web.admin.portalManagement.channelModulesBulk.apply.success',
              {
                succeeded: applyOutcome.succeeded,
                failed: applyOutcome.failed,
              }
            )}
          </M>
        </div>
      )}

      {/* ── Mode selector ────────────────────────────────────────────────── */}
      <div className={styles.modeSelector}>
        <Button
          type="button"
          variant={selection.mode === 'criteria' ? 'primary' : 'tertiary'}
          onClick={() => handleModeChange('criteria')}
        >
          {t('web.admin.portalManagement.channelModulesBulk.mode.criteria')}
        </Button>
        <Button
          type="button"
          variant={selection.mode === 'parent' ? 'primary' : 'tertiary'}
          onClick={() => handleModeChange('parent')}
        >
          {t('web.admin.portalManagement.channelModulesBulk.mode.parent')}
        </Button>
      </div>

      {/* ── Selection panel ──────────────────────────────────────────────── */}
      <div className={styles.selectionPanel}>
        {selection.mode === 'criteria' ? (
          <Table
            isSelectable
            columns={tableColumns}
            data={criteriaTableRows}
            isLoading={selection.criteriaLoading}
            totalRows={selection.criteriaTotal}
            hasKeywordFilter
            filters={CHANNEL_TYPE_FILTERS}
            pagination="server"
            onKeywordFilterChange={handleKeywordFilterChange}
            onFilterChange={handleFilterChange}
            onPageChange={(pageIndex: number) =>
              selection.handlePageChange(pageIndex)
            }
            page={selection.criteria.page}
            pageSize={selection.criteria.pageSize}
            onPageSizeChange={(pageSize: PageSizeType) =>
              selection.handlePageSizeChange(pageSize)
            }
            rowSelectionExternal
            rowSelection={selection.selectedChannelIds}
            getRowId={(row: { _id: string }) => row._id}
            onSelectionChange={(ids: string[]) =>
              selection.setSelectedFromIds(ids)
            }
            tableKey="portalManagement.bulkChannelModulesTable"
          />
        ) : (
          <>
            <div className={styles.parentPickerRow}>
              <span className={styles.parentPickerLabel}>
                {t(
                  'web.admin.portalManagement.channelModulesBulk.mode.parent.label'
                )}
              </span>
              <div className={styles.parentPickerField}>
                <ParentChannelPicker
                  selectedChannelId={selection.parentChannelId}
                  onChannelSelected={ch => selection.handleParentSelected(ch)}
                />
              </div>
            </div>

            {selection.parentLoading && <Loading />}

            {!selection.parentLoading &&
              selection.parentDescendants.length > 0 && (
                <>
                  <S className={styles.descendantsInfo}>
                    {t(
                      'web.admin.portalManagement.channelModulesBulk.mode.parent.descendantCount',
                      {
                        count: selection.parentDescendants.length,
                        total: selection.parentTotal,
                      }
                    )}
                  </S>
                  {selection.parentHasMoreDescendants && (
                    <S className={styles.descendantsLimitWarning}>
                      {t(
                        'web.admin.portalManagement.channelModulesBulk.mode.parent.descendantLimitWarning',
                        {
                          limit: selection.parentLimit,
                          total: selection.parentTotal,
                        }
                      )}
                    </S>
                  )}
                  <Table
                    columns={tableColumns}
                    data={parentTableRows}
                    isLoading={false}
                    totalRows={parentTableRows.length}
                    getRowId={(row: { _id: string }) => row._id}
                    tableKey="portalManagement.bulkChannelModulesParentTable"
                  />
                </>
              )}

            {!selection.parentLoading &&
              selection.parentChannelId &&
              selection.parentDescendants.length === 0 && (
                <S>
                  {t(
                    'web.admin.portalManagement.channelModulesBulk.mode.parent.noDescendants'
                  )}
                </S>
              )}
          </>
        )}
      </div>

      {/* ── Module configuration ──────────────────────────────────────────── */}
      {isInspectionsEnabled && (
        <div className={styles.moduleSection}>
          <H4 className={styles.moduleSectionHeader}>
            {t(
              'web.admin.portalManagement.channelModulesBulk.modules.section.title'
            )}
          </H4>
          <div className={styles.setting}>
            <Toggle
              testId="bulkInspectionsToggle"
              value={inspectionsToggled}
              onChange={setInspectionsToggled}
              text={t(inspectionsModule.labelTitleKey)}
              description={t(inspectionsModule.labelDescriptionKey)}
              doTranslate={false}
              disabled={selection.selectedChannelIds.length === 0}
            />
          </div>
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <div className={styles.actions}>
        <Button
          disabled={!canApply}
          onClick={handleOpenPreview}
          testId="bulkModuleApplyButton"
        >
          {t(
            'web.admin.portalManagement.channelModulesBulk.actions.previewApply'
          )}
        </Button>
        <span className={styles.selectionCount}>
          {t(
            'web.admin.portalManagement.channelModulesBulk.actions.selectedCount',
            { count: selection.selectedChannelIds.length }
          )}
        </span>
      </div>

      {/* ── Preview modal ─────────────────────────────────────────────────── */}
      <BulkModulePreviewModal
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        onConfirm={handleApply}
        applying={applyHook.applying}
        totalTargets={preview.totalTargets}
        alreadyEnabledCount={preview.alreadyEnabledIds.length}
        toEnableCount={preview.toEnableIds.length}
        moduleTitleKey={inspectionsModule.labelTitleKey}
      />
    </div>
  );
}
